import { useState, useEffect } from "react";
import { reportsApi, type ReportSummary, type DepartmentReport } from "@/lib/api";
import ExpiryReport from "./components/ExpiryReport";
import AuditReport from "./components/AuditReport";

type Tab = "expiry" | "audit";

function formatZAR(n: number): string {
  if (n >= 1_000_000_000) return `R ${(n / 1_000_000_000).toFixed(1)}B`;
  if (n >= 1_000_000) return `R ${(n / 1_000_000).toFixed(1)}M`;
  return `R ${n.toLocaleString("en-ZA")}`;
}

export default function Reports() {
  const [activeTab, setActiveTab] = useState<Tab>("expiry");
  const [summary, setSummary] = useState<ReportSummary | null>(null);
  const [deptReports, setDeptReports] = useState<DepartmentReport[]>([]);
  const [loadingSummary, setLoadingSummary] = useState(true);

  useEffect(() => {
    Promise.all([
      reportsApi.summary(),
      reportsApi.byDepartment(),
    ]).then(([summaryRes, deptRes]) => {
      setSummary(summaryRes.data);
      setDeptReports(deptRes.data);
    }).catch(() => {}).finally(() => setLoadingSummary(false));
  }, []);

  return (
    <div>
      <div className="mb-5 flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4">
        <div>
          <h1 className="text-foreground-950 text-xl md:text-2xl font-bold">Reports</h1>
          <p className="text-foreground-500 text-sm mt-1">Generate and export contract expiry lists and audit trail reports</p>
        </div>
        <div className="flex items-center gap-1 bg-background-100 rounded-full p-1">
          <button onClick={() => setActiveTab("expiry")}
            className={`px-4 py-2 rounded-full text-sm font-medium transition-colors cursor-pointer whitespace-nowrap flex items-center gap-1.5 ${activeTab === "expiry" ? "bg-white text-foreground-900 shadow-sm" : "text-foreground-500 hover:text-foreground-700"}`}>
            <i className="ri-timer-flash-line text-sm"></i>Expiry Report
          </button>
          <button onClick={() => setActiveTab("audit")}
            className={`px-4 py-2 rounded-full text-sm font-medium transition-colors cursor-pointer whitespace-nowrap flex items-center gap-1.5 ${activeTab === "audit" ? "bg-white text-foreground-900 shadow-sm" : "text-foreground-500 hover:text-foreground-700"}`}>
            <i className="ri-history-line text-sm"></i>Audit Trail
          </button>
        </div>
      </div>

      {/* Portfolio Summary */}
      {!loadingSummary && summary && (
        <div className="mb-6 space-y-4">
          {/* KPI Row */}
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
            {[
              { label: "Total Contracts", value: summary.total_contracts, color: "text-foreground-900" },
              { label: "Active", value: summary.active_contracts, color: "text-secondary-600" },
              { label: "Decision Pending", value: summary.pending_renewal, color: "text-accent-600" },
              { label: "Expiring (30d)", value: summary.expiring_30, color: "text-accent-700" },
              { label: "Expiring (90d)", value: summary.expiring_90, color: "text-foreground-700" },
              { label: "Expired", value: summary.expired_contracts, color: "text-foreground-500" },
            ].map((item) => (
              <div key={item.label} className="bg-white rounded-lg border border-background-200 p-4">
                <p className="text-foreground-400 text-[11px] font-medium uppercase tracking-wider mb-1">{item.label}</p>
                <p className={`text-2xl font-bold ${item.color}`}>{item.value}</p>
              </div>
            ))}
          </div>

          {/* Value Row */}
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            <div className="bg-white rounded-lg border border-background-200 p-4">
              <p className="text-foreground-400 text-[11px] font-medium uppercase tracking-wider mb-1">Total Portfolio Value</p>
              <p className="text-foreground-900 text-xl font-bold">{formatZAR(summary.total_value)}</p>
              <p className="text-foreground-500 text-xs mt-0.5">all contracts</p>
            </div>
            <div className="bg-white rounded-lg border border-background-200 p-4">
              <p className="text-foreground-400 text-[11px] font-medium uppercase tracking-wider mb-1">Active Contract Value</p>
              <p className="text-secondary-600 text-xl font-bold">{formatZAR(summary.active_value)}</p>
              <p className="text-foreground-500 text-xs mt-0.5">currently active</p>
            </div>
            <div className="bg-white rounded-lg border border-background-200 p-4">
              <p className="text-foreground-400 text-[11px] font-medium uppercase tracking-wider mb-1">At-Risk Value (90d)</p>
              <p className="text-accent-600 text-xl font-bold">{formatZAR(summary.expiring_value_90)}</p>
              <p className="text-foreground-500 text-xs mt-0.5">expiring within 90 days</p>
            </div>
          </div>

          {/* Department Breakdown */}
          {deptReports.length > 0 && (
            <div className="bg-white rounded-lg border border-background-200 overflow-hidden">
              <div className="px-5 py-3 border-b border-background-100">
                <h2 className="text-foreground-800 text-sm font-semibold">By Department</h2>
              </div>
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="bg-background-50 border-b border-background-200">
                      {["Department", "Total", "Active", "Pending", "Expired", "Total Value", "Active Value"].map((h) => (
                        <th key={h} className="text-left px-4 py-2.5 text-foreground-500 text-[11px] font-semibold uppercase tracking-wider whitespace-nowrap">{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {deptReports.map((row, i) => (
                      <tr key={row.department} className={`border-b border-background-100 hover:bg-background-50/50 ${i === deptReports.length - 1 ? "border-b-0" : ""}`}>
                        <td className="px-4 py-2.5 text-foreground-800 text-sm font-medium">{row.department}</td>
                        <td className="px-4 py-2.5 text-foreground-700 text-sm">{row.total_contracts}</td>
                        <td className="px-4 py-2.5 text-secondary-600 text-sm font-medium">{row.active_contracts}</td>
                        <td className="px-4 py-2.5 text-accent-600 text-sm">{row.pending_renewal}</td>
                        <td className="px-4 py-2.5 text-foreground-500 text-sm">{row.expired_contracts}</td>
                        <td className="px-4 py-2.5 text-foreground-700 text-sm font-medium">{formatZAR(parseFloat(row.total_value || "0"))}</td>
                        <td className="px-4 py-2.5 text-foreground-600 text-sm">{formatZAR(parseFloat(row.active_value || "0"))}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Active Report */}
      {activeTab === "expiry" ? <ExpiryReport /> : <AuditReport />}
    </div>
  );
}
